import React, { useCallback, useEffect, useState } from "react";
import { Calculator, RefreshCw, Loader2, FileText, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AttachmentViewer from "./AttachmentViewer";
import { useGallery } from "./AttachmentStrip";

const eur = (n) => (n == null ? "—" : `${Math.round(n).toLocaleString("fr-FR")} €`);
const LEVEL = {
  green: ["Favorable", "bg-emerald-500", "border-emerald-300 bg-emerald-50"],
  orange: ["À surveiller", "bg-amber-400", "border-amber-300 bg-amber-50"],
  red: ["Risqué", "bg-orange-500", "border-orange-300 bg-orange-50"],
  unknown: ["Revenus inconnus", "bg-slate-400", "border-norea-border bg-norea-bg"],
};
const CAT_LABEL = { revenu: "Revenus", logement: "Logement", credit: "Crédits en cours", pension: "Pensions versées", taxe: "Taxe foncière", energie: "Énergie" };

function Amount({ value, overridden, onSave, testid, fmt = eur }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  if (edit) {
    return (
      <input autoFocus type="number" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { setEdit(false); if (v !== "") onSave(parseFloat(v)); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEdit(false); }}
        className="w-24 text-right text-sm border border-norea-primary rounded px-1 py-0.5" data-testid={`${testid}-input`} />
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button onClick={() => { setV(value ?? ""); setEdit(true); }} className={`group tabular-nums text-sm font-medium hover:underline ${overridden ? "text-norea-primary" : "text-norea-text"}`} title="Corriger le montant" data-testid={testid}>
        {fmt(value)} <Pencil className="w-3 h-3 inline opacity-0 group-hover:opacity-60" />
      </button>
      {overridden && <button onClick={() => onSave(null)} title="Revenir à la valeur IA" className="text-norea-textMuted hover:text-norea-text" data-testid={`${testid}-reset`}><RotateCcw className="w-3 h-3" /></button>}
    </span>
  );
}

function Line({ label, note, source, children, onOpen, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-dashed border-norea-border last:border-0" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-sm text-norea-text">{label}</div>
        <div className="text-[11px] text-norea-textMuted flex items-center gap-1 flex-wrap">
          {source && <button onClick={() => onOpen(source.key)} className="inline-flex items-center gap-0.5 text-norea-primary hover:underline" data-testid={`${testid}-source`}><FileText className="w-3 h-3" /> {source.type}</button>}
          {note && <span className="truncate max-w-[22rem]" title={note}>{source ? "· " : ""}{note}</span>}
        </div>
      </div>
      {children}
    </div>
  );
}

const Kpi = ({ label, value, sub, strong, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-norea-textMuted font-semibold">{label}</div>
    <div className={`tabular-nums ${strong ? "text-2xl font-semibold" : "text-lg font-medium"} text-norea-text`}>{value}</div>
    {sub && <div className="text-[11px] text-norea-textMuted">{sub}</div>}
  </div>
);

export default function ResteAVivreCard({ dossierId, reloadKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [gallery] = useGallery(dossierId, reloadKey);
  const load = useCallback(() => api.get(`/dossiers/${dossierId}/reste-a-vivre`).then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || "Calcul indisponible" })), [dossierId]);
  useEffect(() => { setData(null); load(); }, [load, reloadKey]);
  const recalc = async () => {
    setBusy(true);
    try { setData((await api.post(`/dossiers/${dossierId}/reste-a-vivre/recalc`)).data); toast.success("Reste à vivre recalculé"); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const override = async (key, value) => {
    try { setData((await api.patch(`/dossiers/${dossierId}/reste-a-vivre/override`, { key, value })).data); } catch { toast.error("Correction impossible"); }
  };

  const lv = LEVEL[data?.level] || LEVEL.unknown;
  const groups = data?.lines ? Object.keys(CAT_LABEL).map((c) => [c, data.lines.filter((l) => l.cat === c)]).filter(([, ls]) => ls.length) : [];

  return (
    <div className={`card-flat p-5 border ${data?.lines ? lv[2] : ""}`} data-testid="rav-card">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="overline flex items-center gap-1.5"><Calculator className="w-3 h-3" /> Reste à vivre</div>
        <div className="flex items-center gap-2">
          {data?.lines && <span className="inline-flex items-center gap-1.5 text-xs font-semibold" data-testid="rav-level" data-level={data.level}><span className={`w-2.5 h-2.5 rounded-full ${lv[1]}`} /> {lv[0]}</span>}
          <button onClick={recalc} disabled={busy || !data || data.empty} className="btn-secondary text-xs" data-testid="rav-recalc">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Recalculer</button>
        </div>
      </div>
      {!data ? (
        <div className="py-6 text-center text-xs text-norea-textMuted" data-testid="rav-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Analyse des pièces en cours…</div>
      ) : data.error ? (
        <div className="text-xs text-orange-600" data-testid="rav-error">{data.error}</div>
      ) : data.empty ? (
        <div className="text-xs text-norea-textMuted italic" data-testid="rav-empty">Aucune fiche de paie, avis d'impôt ou fiche contact financement reçu pour l'instant.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pb-4 border-b border-norea-border">
            <Kpi label="Reste à vivre / mois" value={eur(data.reste_a_vivre)} strong testid="rav-total" />
            <Kpi label="Par personne" value={eur(data.par_personne)} sub={<Amount value={data.personnes} fmt={(n) => `${n} pers. au foyer`} overridden={data.personnes_overridden} onSave={(v) => override("personnes", v)} testid="rav-personnes" />} testid="rav-pp" />
            <Kpi label="Endettement avant" value={data.endettement_avant == null ? "—" : `${data.endettement_avant} %`} testid="rav-end-avant" />
            <Kpi label="Endettement après projet" value={data.endettement_apres == null ? "—" : `${data.endettement_apres} %`} sub={`seuil ${data.thresholds.endettement_red} %`} testid="rav-end-apres" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
            {groups.map(([cat, ls]) => (
              <div key={cat} data-testid={`rav-group-${cat}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-norea-textMuted mb-1">{cat === "revenu" ? "+ " : "− "}{CAT_LABEL[cat]}</div>
                {ls.map((l) => (
                  <Line key={l.key} label={l.label} note={l.note} source={l.source} onOpen={setOpenKey} testid={`rav-line-${l.key}`}>
                    <Amount value={l.amount} overridden={l.overridden} onSave={(v) => override(l.key, v)} testid={`rav-amount-${l.key}`} />
                  </Line>
                ))}
              </div>
            ))}
            <div data-testid="rav-group-projet">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-norea-textMuted mb-1">− Nouvelle mensualité (projet)</div>
              <Line label="Mensualité du financement" note={data.mensualite.note} source={data.mensualite.source} onOpen={setOpenKey} testid="rav-line-mensualite">
                <Amount value={data.mensualite.amount} overridden={data.mensualite.overridden} onSave={(v) => override("mensualite", v)} testid="rav-amount-mensualite" />
              </Line>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-norea-border flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span>Revenus <b className="tabular-nums">{eur(data.revenus)}</b></span>
            <span>− Charges <b className="tabular-nums">{eur(data.charges)}</b></span>
            <span>− Mensualité <b className="tabular-nums">{eur(data.mensualite.amount)}</b></span>
            <span>= <b className="tabular-nums">{eur(data.reste_a_vivre)}</b></span>
            <span className="ml-auto text-[11px] text-norea-textMuted">Calculé le {new Date(data.computed_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {data.documents.length} pièce(s) analysée(s)</span>
          </div>
        </>
      )}
      {openKey && gallery && <AttachmentViewer dossierId={dossierId} items={gallery} startKey={openKey} onClose={() => setOpenKey(null)} />}
    </div>
  );
}
